import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { Sentry } from "@/lib/sentry";
import { DJAYMA_PHONE, DJAYMA_PHONE_TEL } from "@/lib/constants";
import { Button } from "@cieda/djayma-ui";

// Session storage key used to prevent infinite reload loops for chunk errors
const CHUNK_RELOAD_KEY = "chunk-reload-attempted";

function isChunkLoadError(error: Error): boolean {
  return (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Unable to preload CSS") ||
    error.message.includes("error loading dynamically imported module") ||
    error.name === "ChunkLoadError"
  );
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const chunkError = isChunkLoadError(error);

    if (chunkError) {
      // Auto-reload once to pick up the latest Vite build chunks.
      // Guard against infinite loops by checking sessionStorage.
      const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      if (!alreadyReloaded) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        window.location.reload();
        return;
      }
      // Already tried reloading — fall through to show the update banner
    }

    console.error("Application error:", error, errorInfo);
    // Report to Sentry (no-op if DSN not configured)
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack ?? "" } },
    });
  }

  private handleReload = () => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Chunk load error — show a friendly "new version available" message
      if (this.state.isChunkError) {
        return (
          <div
            className="flex min-h-screen items-center justify-center bg-background p-6"
            aria-live="assertive"
            role="alert"
          >
            <div className="text-center max-w-md">
              <p className="text-5xl mb-4">🔄</p>
              <h1 className="text-2xl font-bold text-foreground mb-3">
                Mise à jour disponible
              </h1>
              <p className="text-muted-foreground mb-6">
                Une nouvelle version de l'application est disponible.
                Rechargez la page pour continuer.
              </p>
              <Button
                variant="secondary"
                onClick={this.handleReload}
                className="px-6 py-3 font-semibold"
              >
                Recharger la page
              </Button>
            </div>
          </div>
        );
      }

      // Generic error
      return (
        <div
          className="flex min-h-screen items-center justify-center bg-background p-6"
          aria-live="assertive"
          role="alert"
        >
          <div className="text-center max-w-md">
            <p className="text-7xl font-bold font-display text-secondary mb-4">500</p>
            <h1 className="text-2xl font-bold text-foreground mb-3">
              Une erreur est survenue
            </h1>
            <p className="text-muted-foreground mb-6">
              Nous sommes désolés, quelque chose s'est mal passé.
              Contactez-nous au{" "}
              <a href={DJAYMA_PHONE_TEL} className="text-secondary hover:underline">
                {DJAYMA_PHONE}
              </a>{" "}
              si le problème persiste.
            </p>
            <Button
              variant="secondary"
              onClick={() => window.location.reload()}
              className="px-6 py-3 font-semibold"
            >
              Rafraîchir la page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
